import { useMemo, useState, PropsWithChildren } from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Amenity,
  AmenityDefinition,
  AmenityDefinitionWeekDaySlot,
  Building,
  CreateAmenityDefinitionData,
  AmenityDefinitionSlotVariation,
  AmenityDefinitionEsclusiveReservationSettings,
  AmenityDefinitionDisclaimer,
} from "../../../../graphql/models";

import useAmenityDefinitionFormStyle from "./amenity_definition_form.style";

import {
  AmenityDefinitionEsclusiveReservationSettingsEditor,
  AmenityDisclaimers,
  AmenityTabPanel,
  GqlRecordPicker,
  I18NTextField,
  ImagePicker,
} from "../../../components";
import { useSnackbarMutation } from "../../../../hooks";
import { useUserState } from "../../../../context";
import { i18nForFields } from "../../../../i18n";
//import DaySlot from './day_slot';

export interface AmenityDefinitionEditFormProps extends PropsWithChildren<any> {
  value: AmenityDefinition | CreateAmenityDefinitionData | undefined;
  onValueUpdatedSuccessfully: (v: AmenityDefinition) => void;
}

export function AmenityDefinitionForm(props: AmenityDefinitionEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useAmenityDefinitionFormStyle();

  const { userRole } = useUserState();
  const isAdmin = useMemo(() => userRole === "system_admin", [userRole]);

  let mutation = MUTATIONS.CREATE_AMENITY_DEFINITION;
  if (value && (value as AmenityDefinition).id) {
    mutation = MUTATIONS.UPDATE_AMENITY_DEFINITION;
  }
  const [
    invokeCreateOrUpdateAmenityDefinition,
    { loading, data: createOrUpdateAmenityDefinitionData },
  ] = useSnackbarMutation(mutation);
  const [updated, setUpdated] = useState(false);

  const [i18nFields, setI18nFields] = useState<Partial<i18nForFields<"name">>>(
    value?.i18n ?? {}
  );
  const [building, setBuilding] = useState<Building | undefined>(
    value?.building ?? void 0
  );
  const [hidden, setHidden] = useState<boolean>(value?.hidden ?? true);
  const [amenity, setAmenity] = useState<Amenity | undefined>(
    value?.serviceId
      ? ({
          id: value.serviceId,
        } as Amenity)
      : void 0
  );
  const [periodFrom, setPeriodFrom] = useState<Date | undefined | null>(
    value?.periodFrom ? new Date(value.periodFrom) : void 0
  );
  const [periodTo, setPeriodTo] = useState<Date | undefined | null>(
    value?.periodTo ? new Date(value.periodTo) : void 0
  );
  const [weekSlots, setWeekSlots] = useState<AmenityDefinitionWeekDaySlot[][]>(
    value?.weekSlots ?? [[], [], [], [], [], [], []]
  );
  const [slotVariations, setSlotVariations] = useState<
    AmenityDefinitionSlotVariation[]
  >(value?.slotVariations ?? []);
  const [exclusiveReservationSettings, setExclusiveReservationSettings] =
    useState<AmenityDefinitionEsclusiveReservationSettings | undefined>(
      value?.exclusiveReservationSettings ?? {
        enabled: false,
        minConsecutiveSlots: 0,
        maxConsecutiveSlots: 0,
        i18n: {
          notes_en: "",
          notes_it: "",
        },
      }
    );
  const [alwaysEsclusive, setAlwaysEsclusive] = useState<boolean>(
    value?.alwaysEsclusive ?? false
  );
  const [minConsecutiveSlots, setMinConsecutiveSlots] = useState<
    number | undefined
  >(value?.minConsecutiveSlots ?? void 0);
  const [maxConsecutiveSlots, setMaxConsecutiveSlots] = useState<
    number | undefined
  >(value?.maxConsecutiveSlots ?? void 0);
  const [slotDurationInMinutes, setSlotDurationInMinutes] = useState<number>(
    value?.slotDurationInMinutes ?? 30
  );
  const [oldImageUrl, setOldImageUrl] = useState<string | undefined>(
    value?.imageUrl
  );
  const [imageUrl, setImageUrl] = useState<string | undefined>(
    value?.image?.url
  );
  const [disclaimers, setDisclaimers] = useState<AmenityDefinitionDisclaimer[]>(
    value?.disclaimers ?? []
  );

  const isNewRecord = useMemo(() => {
    return !!value?.id === false;
  }, [value?.id]);

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateAmenityDefinition({
      variables: {
        id: (value as AmenityDefinition).id,
        amenityDefinitionData: {
          building,
          hidden,
          serviceId: amenity?.id,
          serviceCategoryName:
            amenity?.categoryName ??
            (value as AmenityDefinition).serviceCategoryName,
          disclaimers: disclaimers,
          periodFrom: periodFrom?.getTime(),
          periodTo: periodTo?.getTime(),
          weekSlots,
          slotVariations,
          exclusiveReservationSettings,
          alwaysEsclusive,
          minConsecutiveSlots,
          maxConsecutiveSlots,
          slotDurationInMinutes,
          imageUrl: oldImageUrl,
          image: {
            url: imageUrl,
          },
          i18n: i18nFields,
        },
      },
    });
  };

  if (createOrUpdateAmenityDefinitionData && !updated) {
    onValueUpdatedSuccessfully(
      createOrUpdateAmenityDefinitionData.createSystemAmenityDefinition
    );
    setUpdated(true);
  }

  const saveEnabled = useMemo(() => {
    return (
      amenity &&
      building &&
      periodFrom &&
      periodTo &&
      minConsecutiveSlots &&
      maxConsecutiveSlots
    );
  }, [
    amenity,
    building,
    periodFrom,
    periodTo,
    minConsecutiveSlots,
    maxConsecutiveSlots,
  ]);

  const slotDurationDisabled = useMemo(() => {
    return weekSlots.some((slots) => {
      return slots.length > 0;
    });
  }, [weekSlots]);

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateAmenityDefinitionData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                className={classes.textField}
                label="Nome"
                variant="outlined"
                i18nFieldName="name"
                value={i18nFields}
                onChange={setI18nFields}
              />
            </Grid>
            {isNewRecord && (
              <>
                <Grid item sm={4} xs={12}>
                  <GqlRecordPicker
                    required
                    className={classes.textField}
                    multiple={false}
                    value={amenity}
                    getOptionLabel={(record) => record.name}
                    placeHolder={"Amenity"}
                    renderOption={(record) => record.name}
                    onValueChange={(value) => setAmenity(value)}
                    query={QUERIES.AMENITIES}
                    queryResultDataField={"amenities"}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <GqlRecordPicker
                    required
                    className={classes.textField}
                    multiple={false}
                    value={building}
                    getOptionLabel={(record) => record.name}
                    placeHolder={"Palazzo"}
                    renderOption={(record) => record.name}
                    onValueChange={(value) => setBuilding(value)}
                    query={QUERIES.BUILDINGS("light")}
                    queryResultDataField={"buildings"}
                  />
                </Grid>
              </>
            )}
            <Grid item sm={4} xs={12}>
              <DateTimePicker
                required
                className={classes.datePicker}
                format="dd/MM/yyyy HH:mm"
                label="Dal"
                inputVariant="outlined"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <DateTimePicker
                required
                className={classes.datePicker}
                format="dd/MM/yyyy HH:mm"
                label="Al"
                inputVariant="outlined"
                value={periodTo}
                onChange={(e) => setPeriodTo(e)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                disabled={slotDurationDisabled}
                className={classes.textField}
                label="Durata slot in minuti"
                variant="outlined"
                type="number"
                value={slotDurationInMinutes}
                onChange={(e) =>
                  setSlotDurationInMinutes(Number(e.target.value))
                }
              />
            </Grid>
            {isAdmin && (
              <>
                {false && (
                  <Grid item sm={4} xs={12}>
                    <TextField
                      className={classes.textField}
                      label="Path immagine (obsoleto, verrà rimosso nella prossime versioni)"
                      variant="outlined"
                      type="string"
                      value={oldImageUrl}
                      onChange={(e) => setOldImageUrl(e.target.value?.trim())}
                    />
                  </Grid>
                )}
                <Grid item sm={4} xs={12}>
                  <ImagePicker
                    defaultValue={imageUrl}
                    onUploaded={(entry) => setImageUrl(entry.url)}
                    width={200}
                    height={200}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <AmenityDisclaimers
                    disclaimers={disclaimers}
                    onChange={setDisclaimers}
                  />
                </Grid>
              </>
            )}
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Minimi slot consecutivi"
                variant="outlined"
                type="number"
                value={minConsecutiveSlots}
                onChange={(e) => setMinConsecutiveSlots(Number(e.target.value))}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Massimi slot consecutivi"
                variant="outlined"
                type="number"
                value={maxConsecutiveSlots}
                onChange={(e) => setMaxConsecutiveSlots(Number(e.target.value))}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <Typography variant="subtitle1">
                SEMPRE e SOLO Ad uso eslusivo?
                <Checkbox
                  checked={alwaysEsclusive}
                  onChange={(_evt, checked) =>
                    setAlwaysEsclusive(!alwaysEsclusive)
                  }
                />
              </Typography>
            </Grid>
            <Grid item sm={4} xs={12}>
              <Typography variant="subtitle1">
                Mostra in app?
                <Checkbox
                  checked={!hidden}
                  onChange={(_evt, checked) => setHidden(!checked)}
                />
              </Typography>
            </Grid>
            {!alwaysEsclusive && (
              <Grid item sm={12} xs={12}>
                <AmenityDefinitionEsclusiveReservationSettingsEditor
                  esclusiveReservationSettings={exclusiveReservationSettings}
                  onChange={setExclusiveReservationSettings}
                />
              </Grid>
            )}
            {false && (
              <Grid item sm={12} xs={12}>
                <AmenityTabPanel
                  weekSlots={weekSlots}
                  weekSlotsChange={(updatedWeekSlots) =>
                    setWeekSlots(updatedWeekSlots)
                  }
                  slotsVariations={slotVariations}
                  slotsVariationsChange={(updatedSlotsVariations) =>
                    setSlotVariations(updatedSlotsVariations)
                  }
                  slotDurationInMinutes={slotDurationInMinutes}
                />
              </Grid>
            )}
            <Grid item sm={12} xs={12}>
              <Button
                disabled={!saveEnabled}
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
